import { useCallback, useEffect, useRef, useState } from "react";
import { fetchHolosJob, postHolosJob } from "../Components/axiosFetchers.js";

const NON_TERMINAL_STATUSES = ["PENDING", "SENDING", "RETRY"];
const POLL_INTERVAL_MS = 7000;

// Polls a crop year's Holos GHG calculation job until it reaches a terminal
// status (COMPLETED / FAILED / REJECTED_UNSUPPORTED). `job === null` (with no
// error) is a valid, terminal UI state meaning "no calculation has been run yet".
export default function useHolosJob(cropyearId, user) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const clearPoll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPollIfNeeded = useCallback(
    (currentJob) => {
      clearPoll();
      if (!currentJob || !NON_TERMINAL_STATUSES.includes(currentJob.status)) {
        return;
      }
      intervalRef.current = setInterval(async () => {
        try {
          const latest = await fetchHolosJob(cropyearId, user);
          setJob(latest);
          if (!latest || !NON_TERMINAL_STATUSES.includes(latest.status)) {
            clearPoll();
          }
        } catch (pollError) {
          setError(pollError);
          clearPoll();
        }
      }, POLL_INTERVAL_MS);
    },
    [cropyearId, user, clearPoll]
  );

  useEffect(() => {
    if (!cropyearId || !user) return undefined;

    let cancelled = false;
    async function load() {
      try {
        const initialJob = await fetchHolosJob(cropyearId, user);
        if (cancelled) return;
        setJob(initialJob);
        setError(null);
        startPollIfNeeded(initialJob);
      } catch (fetchError) {
        if (cancelled) return;
        setError(fetchError);
      }
    }
    load();

    return () => {
      cancelled = true;
      clearPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropyearId, user]);

  const recalculate = useCallback(async () => {
    try {
      const newJob = await postHolosJob(cropyearId, user);
      setJob(newJob);
      setError(null);
      startPollIfNeeded(newJob);
      return newJob;
    } catch (postError) {
      setError(postError);
      throw postError;
    }
  }, [cropyearId, user, startPollIfNeeded]);

  return { job, error, recalculate };
}
